/**
 * Story Mode Level Configurations
 * Defines all 30 levels with progressive difficulty and special mechanics
 */

export const DIFFICULTY_SIMILARITY = {
  LOW: 'low',
  MEDIUM: 'medium', 
  HIGH: 'high'
};

export const CONSTRAINT_MODE = {
  NONE: 'none',
  MOVES: 'moves',
  TIME: 'time',
  MOVES_AND_TIME: 'movesAndTime'
};

export const SPECIAL_MECHANIC = {
  TIME_EXTENDER: 'timeExtender',
  AUTO_MATCH: 'autoMatch',
  SOFT_SHUFFLE: 'softShuffle'
};

export const SESSION_TIMER_MODE = {
  NONE: 'none',         // no timer
  PER_BOARD: 'perBoard', // timer resets each board
  CONTINUOUS: 'continuous' // one timer carries across all boards
};

export const SESSION_CONFIGS = [
  {
    id: 'beginner',
    tier: 'beginner',
    label: 'Beginner',
    description: 'One board. No timer. Learn the game.',
    timerMode: SESSION_TIMER_MODE.NONE,
    boards: [
      {
        index: 1,
        rows: 4,
        cols: 3,
        similarity: DIFFICULTY_SIMILARITY.LOW,
        mechanics: [],
        mechanicParams: {}
      }
    ]
  },
  {
    id: 'intermediate',
    tier: 'intermediate',
    label: 'Intermediate',
    description: 'Three boards. Timer per board. Clock cards appear.',
    timerMode: SESSION_TIMER_MODE.PER_BOARD,
    boards: [
      {
        index: 1,
        rows: 4,
        cols: 3,
        similarity: DIFFICULTY_SIMILARITY.LOW,
        timeLimitSeconds: 90,
        mechanics: [],
        mechanicParams: {}
      },
      {
        index: 2,
        rows: 4,
        cols: 4,
        similarity: DIFFICULTY_SIMILARITY.MEDIUM,
        timeLimitSeconds: 90,
        mechanics: [],
        mechanicParams: {}
      },
      {
        index: 3,
        rows: 4,
        cols: 4,
        similarity: DIFFICULTY_SIMILARITY.MEDIUM,
        timeLimitSeconds: 75,
        mechanics: [SPECIAL_MECHANIC.TIME_EXTENDER],
        mechanicParams: {
          timeExtender: { ratio: 0.2, bonusSeconds: 10, bonusMoves: 0 }
        }
      }
    ]
  },
  {
    id: 'advanced',
    tier: 'advanced',
    label: 'Advanced',
    description: 'Five boards. One continuous timer. Specials throughout.',
    timerMode: SESSION_TIMER_MODE.CONTINUOUS,
    totalTimeLimitSeconds: 240,
    boards: [
      {
        index: 1,
        rows: 4,
        cols: 4,
        similarity: DIFFICULTY_SIMILARITY.LOW,
        mechanics: [],
        mechanicParams: {}
      },
      {
        index: 2,
        rows: 4,
        cols: 4,
        similarity: DIFFICULTY_SIMILARITY.MEDIUM,
        mechanics: [SPECIAL_MECHANIC.TIME_EXTENDER],
        mechanicParams: {
          timeExtender: { ratio: 0.2, bonusSeconds: 12, bonusMoves: 0 }
        }
      },
      {
        index: 3,
        rows: 4,
        cols: 5,
        similarity: DIFFICULTY_SIMILARITY.MEDIUM,
        mechanics: [SPECIAL_MECHANIC.TIME_EXTENDER, SPECIAL_MECHANIC.AUTO_MATCH],
        mechanicParams: {
          timeExtender: { ratio: 0.15, bonusSeconds: 10, bonusMoves: 0 },
          autoMatch: { ratio: 0.15, strategy: 'random' }
        }
      },
      {
        index: 4,
        rows: 4,
        cols: 5,
        similarity: DIFFICULTY_SIMILARITY.HIGH,
        mechanics: [SPECIAL_MECHANIC.AUTO_MATCH, SPECIAL_MECHANIC.SOFT_SHUFFLE],
        mechanicParams: {
          autoMatch: { ratio: 0.15, strategy: 'random' },
          softShuffle: { triggerPairsRatio: 0.2, tilesAffected: 4, scope: 'cluster' }
        }
      },
      {
        index: 5,
        rows: 4,
        cols: 5,
        similarity: DIFFICULTY_SIMILARITY.HIGH,
        mechanics: [SPECIAL_MECHANIC.TIME_EXTENDER, SPECIAL_MECHANIC.AUTO_MATCH, SPECIAL_MECHANIC.SOFT_SHUFFLE],
        mechanicParams: {
          timeExtender: { ratio: 0.1, bonusSeconds: 8, bonusMoves: 0 },
          autoMatch: { ratio: 0.1, strategy: 'random' },
          softShuffle: { triggerPairsRatio: 0.15, tilesAffected: 5, scope: 'cluster' }
        }
      }
    ]
  }
];

// Session helpers
export const getSessionConfig = (tierId) =>
  SESSION_CONFIGS.find(s => s.id === tierId) || null;

export const getSessionBoard = (tierId, boardIndex) => {
  const session = getSessionConfig(tierId);
  return session ? session.boards[boardIndex] || null : null;
};

export const STORY_LEVELS = [
  // Levels 1-5: Pure Memory - Small grids, no constraints
  {
    id: 'story-01',
    index: 1,
    rows: 2,
    cols: 2,
    similarity: DIFFICULTY_SIMILARITY.LOW,
    constraintMode: CONSTRAINT_MODE.NONE,
    mechanics: [],
    tutorialIds: ['storyModeIntro']
  },
  {
    id: 'story-02',
    index: 2,
    rows: 2,
    cols: 3,
    similarity: DIFFICULTY_SIMILARITY.LOW,
    constraintMode: CONSTRAINT_MODE.NONE,
    mechanics: []
  },
  {
    id: 'story-03',
    index: 3,
    rows: 3,
    cols: 2,
    similarity: DIFFICULTY_SIMILARITY.LOW,
    constraintMode: CONSTRAINT_MODE.NONE,
    mechanics: []
  },
  {
    id: 'story-04',
    index: 4,
    rows: 3,
    cols: 3,
    similarity: DIFFICULTY_SIMILARITY.LOW,
    constraintMode: CONSTRAINT_MODE.NONE,
    mechanics: []
  },
  {
    id: 'story-05',
    index: 5,
    rows: 3,
    cols: 3,
    similarity: DIFFICULTY_SIMILARITY.MEDIUM,
    constraintMode: CONSTRAINT_MODE.NONE,
    mechanics: []
  },

  // Levels 6-10: Add Constraints - Generous limits
  {
    id: 'story-06',
    index: 6,
    rows: 3,
    cols: 4,
    similarity: DIFFICULTY_SIMILARITY.LOW,
    constraintMode: CONSTRAINT_MODE.MOVES,
    maxMoves: 20,
    mechanics: []
  },
  {
    id: 'story-07',
    index: 7,
    rows: 3,
    cols: 4,
    similarity: DIFFICULTY_SIMILARITY.LOW,
    constraintMode: CONSTRAINT_MODE.MOVES,
    maxMoves: 18,
    mechanics: []
  },
  {
    id: 'story-08',
    index: 8,
    rows: 4,
    cols: 3,
    similarity: DIFFICULTY_SIMILARITY.MEDIUM,
    constraintMode: CONSTRAINT_MODE.MOVES,
    maxMoves: 16,
    mechanics: []
  },
  {
    id: 'story-09',
    index: 9,
    rows: 4,
    cols: 3,
    similarity: DIFFICULTY_SIMILARITY.MEDIUM,
    constraintMode: CONSTRAINT_MODE.MOVES,
    maxMoves: 15,
    mechanics: []
  },
  {
    id: 'story-10',
    index: 10,
    rows: 4,
    cols: 3,
    similarity: DIFFICULTY_SIMILARITY.MEDIUM,
    constraintMode: CONSTRAINT_MODE.MOVES,
    maxMoves: 14,
    mechanics: []
  },

  // Levels 11-15: Real Constraints - Tighter limits
  {
    id: 'story-11',
    index: 11,
    rows: 4,
    cols: 3,
    similarity: DIFFICULTY_SIMILARITY.MEDIUM,
    constraintMode: CONSTRAINT_MODE.MOVES,
    maxMoves: 12,
    mechanics: []
  },
  {
    id: 'story-12',
    index: 12,
    rows: 4,
    cols: 4,
    similarity: DIFFICULTY_SIMILARITY.MEDIUM,
    constraintMode: CONSTRAINT_MODE.MOVES,
    maxMoves: 20,
    mechanics: []
  },
  {
    id: 'story-13',
    index: 13,
    rows: 4,
    cols: 4,
    similarity: DIFFICULTY_SIMILARITY.MEDIUM,
    constraintMode: CONSTRAINT_MODE.MOVES,
    maxMoves: 18,
    mechanics: []
  },
  {
    id: 'story-14',
    index: 14,
    rows: 4,
    cols: 4,
    similarity: DIFFICULTY_SIMILARITY.MEDIUM,
    constraintMode: CONSTRAINT_MODE.MOVES,
    maxMoves: 16,
    mechanics: []
  },
  {
    id: 'story-15',
    index: 15,
    rows: 4,
    cols: 4,
    similarity: DIFFICULTY_SIMILARITY.MEDIUM,
    constraintMode: CONSTRAINT_MODE.MOVES_AND_TIME,
    maxMoves: 16,
    timeLimitSeconds: 60,
    mechanics: []
  },

  // Levels 16-20: High Similarity - Visual challenge
  {
    id: 'story-16',
    index: 16,
    rows: 4,
    cols: 4,
    similarity: DIFFICULTY_SIMILARITY.HIGH,
    constraintMode: CONSTRAINT_MODE.MOVES,
    maxMoves: 15,
    mechanics: []
  },
  {
    id: 'story-17',
    index: 17,
    rows: 4,
    cols: 4,
    similarity: DIFFICULTY_SIMILARITY.HIGH,
    constraintMode: CONSTRAINT_MODE.MOVES,
    maxMoves: 14,
    mechanics: []
  },
  {
    id: 'story-18',
    index: 18,
    rows: 4,
    cols: 4,
    similarity: DIFFICULTY_SIMILARITY.HIGH,
    constraintMode: CONSTRAINT_MODE.MOVES_AND_TIME,
    maxMoves: 14,
    timeLimitSeconds: 50,
    mechanics: []
  },
  {
    id: 'story-19',
    index: 19,
    rows: 4,
    cols: 5,
    similarity: DIFFICULTY_SIMILARITY.HIGH,
    constraintMode: CONSTRAINT_MODE.MOVES,
    maxMoves: 22,
    mechanics: []
  },
  {
    id: 'story-20',
    index: 20,
    rows: 4,
    cols: 5,
    similarity: DIFFICULTY_SIMILARITY.HIGH,
    constraintMode: CONSTRAINT_MODE.MOVES_AND_TIME,
    maxMoves: 20,
    timeLimitSeconds: 45,
    mechanics: []
  },

  // Levels 21-24: Introduce Time-Extender - Magical help
  {
    id: 'story-21',
    index: 21,
    rows: 4,
    cols: 4,
    similarity: DIFFICULTY_SIMILARITY.MEDIUM,
    constraintMode: CONSTRAINT_MODE.TIME,
    timeLimitSeconds: 40,
    mechanics: [SPECIAL_MECHANIC.TIME_EXTENDER],
    mechanicParams: {
      timeExtender: {
        ratio: 0.25, // 25% of pairs are Time-Extender
        bonusSeconds: 8,
        bonusMoves: 3
      }
    },
    tutorialIds: ['timeExtender']
  },
  {
    id: 'story-22',
    index: 22,
    rows: 4,
    cols: 4,
    similarity: DIFFICULTY_SIMILARITY.MEDIUM,
    constraintMode: CONSTRAINT_MODE.MOVES_AND_TIME,
    maxMoves: 16,
    timeLimitSeconds: 45,
    mechanics: [SPECIAL_MECHANIC.TIME_EXTENDER],
    mechanicParams: {
      timeExtender: {
        ratio: 0.2,
        bonusSeconds: 6,
        bonusMoves: 2
      }
    }
  },
  {
    id: 'story-23',
    index: 23,
    rows: 4,
    cols: 4,
    similarity: DIFFICULTY_SIMILARITY.MEDIUM,
    constraintMode: CONSTRAINT_MODE.MOVES_AND_TIME,
    maxMoves: 15,
    timeLimitSeconds: 40,
    mechanics: [SPECIAL_MECHANIC.TIME_EXTENDER],
    mechanicParams: {
      timeExtender: {
        ratio: 0.15,
        bonusSeconds: 5,
        bonusMoves: 2
      }
    }
  },
  {
    id: 'story-24',
    index: 24,
    rows: 4,
    cols: 5,
    similarity: DIFFICULTY_SIMILARITY.MEDIUM,
    constraintMode: CONSTRAINT_MODE.MOVES_AND_TIME,
    maxMoves: 18,
    timeLimitSeconds: 50,
    mechanics: [SPECIAL_MECHANIC.TIME_EXTENDER],
    mechanicParams: {
      timeExtender: {
        ratio: 0.15,
        bonusSeconds: 4,
        bonusMoves: 2
      }
    }
  },

  // Levels 25-27: Introduce Auto-Match - Strategic help
  {
    id: 'story-25',
    index: 25,
    rows: 4,
    cols: 4,
    similarity: DIFFICULTY_SIMILARITY.MEDIUM,
    constraintMode: CONSTRAINT_MODE.MOVES,
    maxMoves: 14,
    mechanics: [SPECIAL_MECHANIC.AUTO_MATCH],
    mechanicParams: {
      autoMatch: {
        ratio: 0.2,
        strategy: 'random'
      }
    },
    tutorialIds: ['autoMatch']
  },
  {
    id: 'story-26',
    index: 26,
    rows: 4,
    cols: 4,
    similarity: DIFFICULTY_SIMILARITY.MEDIUM,
    constraintMode: CONSTRAINT_MODE.MOVES_AND_TIME,
    maxMoves: 14,
    timeLimitSeconds: 35,
    mechanics: [SPECIAL_MECHANIC.AUTO_MATCH],
    mechanicParams: {
      autoMatch: {
        ratio: 0.15,
        strategy: 'random'
      }
    }
  },
  {
    id: 'story-27',
    index: 27,
    rows: 4,
    cols: 5,
    similarity: DIFFICULTY_SIMILARITY.MEDIUM,
    constraintMode: CONSTRAINT_MODE.MOVES_AND_TIME,
    maxMoves: 16,
    timeLimitSeconds: 40,
    mechanics: [SPECIAL_MECHANIC.AUTO_MATCH],
    mechanicParams: {
      autoMatch: {
        ratio: 0.15,
        strategy: 'random'
      }
    }
  },

  // Levels 28-30: Introduce Soft Shuffle - Disruptor mechanic
  {
    id: 'story-28',
    index: 28,
    rows: 4,
    cols: 4,
    similarity: DIFFICULTY_SIMILARITY.MEDIUM,
    constraintMode: CONSTRAINT_MODE.MOVES_AND_TIME,
    maxMoves: 13,
    timeLimitSeconds: 35,
    mechanics: [SPECIAL_MECHANIC.TIME_EXTENDER, SPECIAL_MECHANIC.AUTO_MATCH],
    mechanicParams: {
      timeExtender: {
        ratio: 0.1,
        bonusSeconds: 4,
        bonusMoves: 2
      },
      autoMatch: {
        ratio: 0.1,
        strategy: 'random'
      }
    }
  },
  {
    id: 'story-29',
    index: 29,
    rows: 4,
    cols: 4,
    similarity: DIFFICULTY_SIMILARITY.MEDIUM,
    constraintMode: CONSTRAINT_MODE.MOVES_AND_TIME,
    maxMoves: 12,
    timeLimitSeconds: 35,
    mechanics: [SPECIAL_MECHANIC.TIME_EXTENDER, SPECIAL_MECHANIC.SOFT_SHUFFLE],
    mechanicParams: {
      timeExtender: {
        ratio: 0.1,
        bonusSeconds: 3,
        bonusMoves: 1
      },
      softShuffle: {
        triggerPairsRatio: 0.15,
        tilesAffected: 4,
        scope: 'cluster'
      }
    },
    tutorialIds: ['softShuffle']
  },
  {
    id: 'story-30',
    index: 30,
    rows: 4,
    cols: 5,
    similarity: DIFFICULTY_SIMILARITY.HIGH,
    constraintMode: CONSTRAINT_MODE.MOVES_AND_TIME,
    maxMoves: 15,
    timeLimitSeconds: 40,
    mechanics: [SPECIAL_MECHANIC.AUTO_MATCH, SPECIAL_MECHANIC.SOFT_SHUFFLE],
    mechanicParams: {
      autoMatch: {
        ratio: 0.1,
        strategy: 'random'
      },
      softShuffle: {
        triggerPairsRatio: 0.12,
        tilesAffected: 5,
        scope: 'cluster'
      }
    },
    tutorialIds: ['finalChallenge']
  }
];

// Helper functions
export const getLevelConfig = (levelIndex) => {
  return STORY_LEVELS.find(level => level.index === levelIndex) || null;
};

export const getTotalLevels = () => STORY_LEVELS.length;

export const getNextLevel = (currentLevelIndex) => {
  return getLevelConfig(currentLevelIndex + 1);
};

export const getPreviousLevel = (currentLevelIndex) => {
  return getLevelConfig(currentLevelIndex - 1);
};

export const isLastLevel = (levelIndex) => {
  return levelIndex >= STORY_LEVELS.length;
};

export const getLevelProgress = (currentLevelIndex) => {
  return {
    current: currentLevelIndex,
    total: STORY_LEVELS.length,
    percentage: Math.round((currentLevelIndex / STORY_LEVELS.length) * 100)
  };
};